import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatingDots from '~/components/AnimatingDots';
import BillingBanner from '~/components/BillingBanner';
import Button from '~/components/Button';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import MainContainer from '~/components/MainContainer';
import PaginationBar from '~/components/PaginationBar';
import SideModal from '~/components/SideModal';
import { MobileCtaContext } from '~/components/SidebarLayout';
import { ADD_ESTIMATE_ROUTE, BILLING_ROUTE } from '~/constants/routes';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import DeleteEstimate from './DeleteEstimate';
import EmptyEstimateList from './EmptyEstimateList';
import EstimateDetails from './EstimateDetails';
import EstimateList from './EstimateList';
import EstimateListFilters from './EstimateListFilters';
import EstimateTable from './EstimateTable';
import { useEstimateList } from './hooks/useEstimateList';
import { EstimateListContext } from './hooks/useEstimateListContext';

const Estimate = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const handleAddEstimate = () => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Estimates',
      button_name: 'New Estimate',
      button_id: 'new_estimate',
      section: 'Header',
    });

    if (canCreateEstimate) {
      navigate(ADD_ESTIMATE_ROUTE);
    } else {
      navigate(`${BILLING_ROUTE}?ref=estimates`);
    }
  };

  const handleBillingBannerClick = () => {
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: 'Estimates',
      link_name: 'Upgrade now',
      link_id: 'upgrade_banner',
      section: 'Banner',
    });
    navigate(`${BILLING_ROUTE}?ref=estimates`);
  };

  const { setMobileCta } = useContext(MobileCtaContext);

  const {
    isLoading,
    listData,
    listIds,
    totalPages,
    totalCount,
    currentPage,
    fetchSpecificPage,
    deleteEstimateId,
    setDeleteEstimateId,
    onEditListEstimate,
    viewEstimateId,
    handleStatusChange,
    setViewEstimateId,
    canCreateEstimate,
    convertEstimateToInvoice,
  } = useEstimateList();

  function onOpenDeleteEstimateModal(estimateUuid: string) {
    setDeleteEstimateId(estimateUuid);
  }

  function onOpenViewEstimateModal(estimateUuid: string) {
    setViewEstimateId(estimateUuid);
  }

  function onEditEstimate(estimateUuid: string) {
    navigate(`${ADD_ESTIMATE_ROUTE}?editUuid=${estimateUuid}`);
  }

  useEffect(() => {
    setMobileCta(
      <Button variant="primary" size="sm" className="full-width" onClick={handleAddEstimate}>
        + New Estimate
      </Button>,
    );
    return () => setMobileCta(null);
  }, [canCreateEstimate, navigate]);

  return (
    <div>
      <Header
        headerText="Estimates"
        rightJsx={
          isDesktop && (
            <Button variant="primary" size="smd" onClick={handleAddEstimate}>
              + New Estimate
            </Button>
          )
        }
      />

      <MainContainer>
        {!canCreateEstimate && (
          <BillingBanner
            variant="error"
            title="Estimate Limit Reached. Ready to Grow? 🚀"
            description="You've successfully created estimates on the Free Plan, great job! Upgrade now to keep creating estimates and continue growing your business."
            buttonText="Upgrade now"
            onButtonClick={handleBillingBannerClick}
          />
        )}

        <div>
          <DeleteEstimate
            estimateUuid={deleteEstimateId}
            handleClose={() => {
              setDeleteEstimateId(null);
            }}
          />

          <EstimateListContext.Provider
            value={{
              listIds,
              listData,
              onOpenDeleteEstimateModal,
              onEditEstimate,
              onEditListEstimate,
              onOpenViewEstimateModal,
              handleStatusChange,
              convertEstimateToInvoice,
              maxListItems: null,
              renderFilters: true,
            }}
          >
            <div className="full-width">
              <SideModal
                size="sm"
                isOpen={viewEstimateId !== null}
                onClose={() => {
                  setViewEstimateId(null);
                }}
                children={<EstimateDetails estimateUuid={viewEstimateId} />}
                heading={{ text: 'Estimate Details' }}
              />
              {isDesktop && <EstimateListFilters />}

              {!isLoading && listIds.length === 0 && <EmptyEstimateList />}

              {listIds.length > 0 && (
                <>
                  {isDesktop ? <EstimateTable /> : <EstimateList />}

                  <PaginationBar
                    totalItems={totalCount}
                    totalPages={totalPages}
                    itemsPerPage={10}
                    currentPage={currentPage}
                    onPageChange={fetchSpecificPage}
                    onItemsPerPageChange={() => {}}
                  />
                </>
              )}
            </div>
          </EstimateListContext.Provider>

          {isLoading && (
            <div className="full-width center">
              <AnimatingDots />
            </div>
          )}

          <Gap t={32} />
        </div>
      </MainContainer>
    </div>
  );
};

export default Estimate;
